import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Alert } from '@/shared/ui/Alert';
import { DataTable } from '@/shared/ui/DataTable';
import { EmptyState } from '@/shared/ui/EmptyState';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SegmentedFilter } from '@/shared/ui/SegmentedFilter';
import { SkeletonRows } from '@/shared/ui/Skeleton';
import { StatusBadge } from '@/shared/ui/StatusBadge';

import type { Execution } from '@/shared/api/generated/rules';
import {
  SOURCE_FILTERS,
  matchesSource,
  sourceOf,
  type SourceFilter,
} from '@/features/scheduling';
import { useExecutionFindings, useExecutions } from '../api/rules';

import {
  executionTone,
  outcomeKey,
  outcomeTone,
  runOutcome,
  shortRunId,
  startedLabel,
} from '../model/presentation';

function ExecutionProgressBar({ execution }: { execution: Execution }): ReactElement {
  const { total, succeeded, failed, cancelled } = execution.progress;
  const finished = succeeded + failed + cancelled;

  /* **Not started is not 0%, and the bar must not be what decides which a reader sees.** A queued
     run gets a hatched, empty track — this design system's mark for every unmeasured figure — where
     a 0%-filled bar is indistinguishable from a run that measured nothing. */
  const queued = finished === 0 && execution.state === 'pending';
  const pct = total > 0 ? Math.min(100, Math.round((finished / total) * 100)) : 0;

  /* 104 x 6, square, no figure above it — the reference's own geometry. The counts live in the Tasks
     column three cells left, and the same information twice in one row is what makes a table dense
     rather than informative. The percentage is in the accessible name, so it is not lost to a reader
     who cannot see the fill. */
  return (
    <div
      className={queued ? 'lq-runbar lq-runbar--queued' : 'lq-runbar'}
      role="progressbar"
      aria-valuenow={queued ? undefined : pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={
        queued ? 'Not started' : `${String(finished)} of ${String(total)} tasks finished`
      }
    >
      {queued ? null : (
        <div
          className={`lq-runbar__fill lq-runbar__fill--${executionTone(execution.state)}`}
          style={{ width: `${String(pct)}%` }}
        />
      )}
    </div>
  );
}

export function ExecutionsScreen(): ReactElement {
  const { t } = useTranslation();
  const executions = useExecutions();
  const findings = useExecutionFindings();
  /* **Client-side, because the route takes no source parameter and inventing one would be a
     parallel backend.** The listing is one bounded page, so filtering it here is exact for
     what is on screen — and the count beside the filter says so rather than implying it
     filtered the whole history. */
  const [source, setSource] = useState<SourceFilter>('all');

  /* Keyed by run, so a total cannot be paired with the wrong row by index. The two lists are the
     same page in the same order by construction — the route takes its ids from the listing's own
     query — and keying on the id is what keeps that true if either ever reorders. */
  const findingsById = new Map(
    (findings.data?.items ?? []).map((each) => [each.execution_id, each]),
  );

  /* One clock read per render, passed down. Reading `new Date()` inside the row would give the rows
     of one table different "todays" across a midnight boundary. */
  const now = new Date();

  const shown = (executions.data?.items ?? []).filter((execution) =>
    matchesSource(execution.requested_by, source),
  );

  return (
    <>
      {/* Through the catalogue, like every other header. These two strings were **literal English
          in a `.tsx`**, which this codebase forbids and which no gate can see: `copy.test.ts`
          sweeps the catalogue for malformed messages and cannot know a screen never asked for one.
          The title also disagreed with its own nav entry — "Quality Executions & Audits" against
          "Executions" — which is how a reader ends up unsure they are on the page they clicked. */}
      <PageHeader title={t('executions.title')} lede={t('executions.lede')} />

      {/* **Manual / Scheduled / After harvest, and the filter exists because the question does.**
          "Did the nightly sweep run" and "what did somebody kick off by hand" are the two things a
          reader comes to this ledger to separate, and before `requested_by` reached the wire
          neither was answerable. `ledger` variant to match the framed table below it. */}
      <SegmentedFilter
        variant="ledger"
        label={t('scheduling.source.column')}
        value={source}
        onChange={setSource}
        options={SOURCE_FILTERS.map((id) => ({
          id,
          label: t(`scheduling.source.${id}`),
        }))}
      />

      {/* **The plan panel is gone, and the way to run rules is not.** It was a "Preview coverage"
          form above this table — three controls, a checkbox tree of every resolved target, and a
          Run button — and the reference design has none of it: this screen is a ledger of runs that
          happened. Starting one now belongs where a reader is already looking at the thing to run,
          which is the `Run` button beside each rule on the Rules screen and on a dataset's own Rules
          tab. Nothing was deleted from the API; `usePlanExecution` and `useRequestExecution` are
          still what those buttons call. */}
      <div className="lq-stack">
        {executions.isError && (
          <Alert tone="error" title={t('executions.unreachable')}>
            {executions.error.message}
          </Alert>
        )}

        {executions.isPending && (
          <>
            <p role="status" className="lq-muted">
              Loading execution history…
            </p>
            <SkeletonRows rows={4} columns={5} />
          </>
        )}

        {executions.isSuccess &&
          (shown.length === 0 ? (
            <EmptyState
              title={t('executions.empty.title')}
              guidance={t('executions.empty.guidance')}
            />
          ) : (
            <DataTable
              variant="ledger"
              caption={t('executions.tableCaption')}
              columns={[
                /* The reference's six. "Open" is its own column *and* the row is clickable — the
                   design does both, and it is right: the row is the target a reader aims at and the
                   link is what tells them the row is a target at all. */
                { key: 'run', label: t('executions.column.run') },
                { key: 'started', label: t('executions.column.started') },
                /* **Why this run happened, which nothing else on the row answers.** A
                   nightly schedule, a harvest that finished three minutes ago and
                   somebody pressing a button are three different facts with three
                   different follow-ups, and before `requested_by` reached the wire a
                   reader could not tell them apart. Third column, beside Started, because
                   "when" and "why" are read together. */
                { key: 'source', label: t('scheduling.source.column') },
                { key: 'tasks', label: t('executions.column.tasks') },
                { key: 'progress', label: t('executions.column.progress') },
                { key: 'result', label: t('executions.column.result'), actions: true },
                { key: 'open', label: '', actions: true },
              ]}
            >
              {shown.map((execution) => {
                const { total, succeeded, failed, cancelled } = execution.progress;
                const finished = succeeded + failed + cancelled;
                const pending = Math.max(0, total - finished);
                const findings = findingsById.get(execution.id);
                const outcome = runOutcome(execution, findings);
                const started = startedLabel(execution.started_at, now);

                return (
                  /* The in-flight row is tinted, as the reference tints it: on a list that is mostly
                     finished history, the one still moving is the one a reader came to look at. */
                  <tr
                    key={execution.id}
                    className={outcome.kind === 'running' ? 'lq-row--live' : undefined}
                  >
                    <td className="lq-mono">{shortRunId(execution.id)}</td>
                    <td className="lq-nowrap lq-cell--quiet">
                      {/* Relative, because "did the nightly sweep run" is what this column is for
                          and a full timestamp makes a reader do date arithmetic to answer it. The
                          exact instant is on hover. */}
                      {started === null ? (
                        t('executions.queued')
                      ) : (
                        <span title={execution.started_at ?? undefined}>{started}</span>
                      )}
                    </td>
                    <td>
                      {/* **Three treatments for three origins, and the split is the audit
                          screen's.** A person reads at weight 500 in the body face; an unattended
                          actor in mono and muted, because "who did this" has a different follow-up
                          for a timer than for a colleague and the two must not look alike. A
                          `system:` subject this build does not recognise is "not reported" rather
                          than a guess — never rendered as Manual, which would put a person's name
                          on a scan nobody ran. */}
                      <span
                        className={`lq-sched__src lq-sched__src--${
                          sourceOf(execution.requested_by) === 'manual'
                            ? 'person'
                            : sourceOf(execution.requested_by) === 'unknown'
                              ? 'unknown'
                              : 'system'
                        }`}
                        title={
                          sourceOf(execution.requested_by) === 'unknown'
                            ? t('scheduling.source.unknownMeaning')
                            : execution.requested_by
                        }
                      >
                        {sourceOf(execution.requested_by) === 'unknown'
                          ? t('scheduling.source.unknown')
                          : execution.requested_by}
                      </span>
                    </td>
                    <td className="lq-cell--quiet">
                      {/* Words, and zero-valued parts omitted: "0 failed" beside "6 succeeded"
                          invites a reader to look for a failure that is not there. */}
                      {[
                        succeeded > 0
                          ? t('executions.tasks.succeeded', { count: succeeded })
                          : null,
                        failed > 0
                          ? t('executions.tasks.failed', { count: failed })
                          : null,
                        pending > 0
                          ? t('executions.tasks.pending', { count: pending })
                          : null,
                        cancelled > 0
                          ? t('executions.tasks.cancelled', { count: cancelled })
                          : null,
                      ]
                        .filter((part): part is string => part !== null)
                        .join(' \u00b7 ') || t('executions.tasks.none')}
                    </td>
                    <td>
                      <ExecutionProgressBar execution={execution} />
                    </td>
                    <td className="lq-table__actions">
                      {/* **The answer a reader came for: did this find anything.** It was the run's
                          *state*, because `Execution` carries no violation total and getting one per
                          row was an N+1 — recorded at the time and fixed properly since, by
                          `GET /v1/rules/execution-findings` summing them for a whole page in one
                          request. */}
                      <StatusBadge
                        shape="square"
                        tone={outcomeTone(outcome)}
                        label={
                          outcome.kind === 'violations'
                            ? t('executions.outcome.violations', { count: outcome.count })
                            : t(outcomeKey(outcome))
                        }
                        title={t(`${outcomeKey(outcome)}Meaning`)}
                      />
                    </td>
                    <td className="lq-table__actions">
                      <Link to={`/executions/${execution.id}`}>
                        {t('executions.open')}
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </DataTable>
          ))}

        {/* The reference's own footnote, and the sentence this whole column exists to hold. */}
        {executions.isSuccess && executions.data.items.length > 0 ? (
          <p className="lq-runnote">{t('executions.note')}</p>
        ) : null}
      </div>
    </>
  );
}
